import { HttpClient, HttpEvent, HttpEventType, HttpParams, HttpRequest, HttpResponse } from '@angular/common/http';
import { EventEmitter, Injectable, Output } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, catchError, of, switchMap, take, timer } from 'rxjs';
import { AppConfigService } from './app-config.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CustomSnackbarComponent } from './common/custom-snackbar/custom-snackbar.component';
import { StateChange } from 'ng-lazyload-image';

@Injectable({
  providedIn: 'root'
})
export class CommonService {
  appConfig: any = {};
  ProjectLSData: any = {};
  resultType: string = 'manual';
  userRoleList = this.ProjectLSData?.userRoleList || '{}';
  defaultRole = this.userRoleList?.length ? this.userRoleList[0] : '';
  openSelectEvent = new EventEmitter<string>();
  onRepoSearchClick = new EventEmitter<string>();
  onIndvSearchClick = new EventEmitter<string>();
  onFolderNavigation = new EventEmitter<string>();
  selectedSource: any;
  projectName: string = '';
  hideDownload: boolean = false;
  metadataSelected: any = {};
  refDataList: any;

  updatedAppVersion?: string;
  showRefreshPanel: boolean = false;


  private readonly currentRoleSource = new BehaviorSubject(this.defaultRole.role);
  currentRole = this.currentRoleSource.asObservable();

  private readonly selectedLocation = new BehaviorSubject('');
  currentPlace = this.selectedLocation.asObservable();

  private readonly selectedScannerSource = new BehaviorSubject({});
  currentSource = this.selectedScannerSource.asObservable();

  private readonly licenseStatus = new BehaviorSubject(null);
  currentLicenseStatus = this.licenseStatus.asObservable();


  private readonly galleryRefresh = new BehaviorSubject('');
  autoRefresh = this.galleryRefresh.asObservable();

  preChangeIconState: boolean = false;
  galleryPreview: any = '';
  totalProcessedImgs: number = 0;
  totalUnprocessedImgs: number = 0;
  totalSelectedImgs: number = 0;
  dashboardUserPrefChanged: boolean = false;

  public finalImgFiles: any = [];
  public uploadIndex: any = 0;

  public browserTimeZone: any;

  public showDashboardSave = new BehaviorSubject(false);
  showDashboardSaveEvent = this.showDashboardSave.asObservable();
  private licensePollingSubscription?: Subscription;

  constructor(private readonly http: HttpClient, private readonly configService: AppConfigService, private readonly snackBar: MatSnackBar) {
    this.appConfig = configService.config;
    this.projectName = this.appConfig.projectName;
    this.fetchProjectLSData();
    if (this.ProjectLSData?.currentLicenseStatus) {
      this.licenseStatus.next(this.ProjectLSData.currentLicenseStatus);
    }
    if (this.ProjectLSData?.currentSelectedSource) {
      this.setCurrentSource(this.ProjectLSData?.currentSelectedSource);
    }
    if (this.ProjectLSData?.galleryPreview) {
      this.galleryPreview = this.ProjectLSData?.galleryPreview;
      this.setGalleryPreview(this.ProjectLSData?.galleryPreview);
    } else {
      this.galleryPreview = 'Grid';
      this.setGalleryPreview('Grid');
    }

    this.browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  setGalleryPreview(preview: any) {
    this.ProjectLSData["galleryPreview"] = preview;
    this.setProjectLSData();
  }

  fetchProjectLSData() {
    if (localStorage[this.projectName]) {
      this.ProjectLSData = JSON.parse(localStorage[this.projectName]);
    }
  }

  setCurrentPlace(place: string) {
    this.ProjectLSData["userActivePlace"] = place;
    this.setProjectLSData();
    this.selectedLocation.next(place);
  }

  refreshGalleryCall(flag: any) {
    this.ProjectLSData["refreshGallery"] = flag;
    this.setProjectLSData();
    this.galleryRefresh.next(flag);
  }

  setCurrentSource(source: any) {
    if (source) {
      this.ProjectLSData["currentSelectedSource"] = source;
      this.setProjectLSData();
    }
    this.selectedScannerSource.next(source);
  }
  setCurrentLicenseStatus(status: any) {
    const existingStatus = this.ProjectLSData["currentLicenseStatus"] || null;
    const hasChanged = JSON.stringify(existingStatus) !== JSON.stringify(status);

    if (status) {
      this.ProjectLSData["currentLicenseStatus"] = status;
    } else {
      delete this.ProjectLSData["currentLicenseStatus"];
    }

    if (hasChanged) {
      this.setProjectLSData();
    }

    this.licenseStatus.next(status);
  }

  getUploadedImages() {
    return this.http.get(`${this.appConfig.apiUrl}slide/SlideList?imageSource=upload`);
  }

  checkImagesForUpload(imageNames: any) {
    let imageFile = imageNames;
    let key = "imageFile";
    let imagesData = {
      [key]: imageFile
    };
    return this.http.post(`${this.appConfig.apiUrl}slide/uploadcheck`, imagesData);
  }

  uploadImages(images: any): Observable<HttpEvent<any>> {
    const req = new HttpRequest('POST', `${this.appConfig.apiUrl}slide/upload`, images, {
      reportProgress: true,
      responseType: 'json'
    });

    return this.http.request(req);
  }

  uploadImage(reloadList: any) {
    if (this.uploadIndex < this.finalImgFiles.length) {
      if (!this.finalImgFiles[this.uploadIndex].isFileExist || this.finalImgFiles[this.uploadIndex].alreadySelected) {
        this.uploadIndex += 1;
        this.uploadImage(reloadList);
        return;
      }
      const frmData = new FormData();
      frmData.append("imageFile", this.finalImgFiles[this.uploadIndex]);
      frmData.append("imageSource", 'upload');

      this.finalImgFiles[this.uploadIndex].inprogress = true;
      this.uploadImages(frmData).subscribe((item: any) => {
        if (item.type === HttpEventType.UploadProgress) {
          this.finalImgFiles[this.uploadIndex].progressValue = Math.round(100 * item.loaded / item.total);
        }
        else if (item instanceof HttpResponse) {
          if (item.status == 200) {
            this.finalImgFiles[this.uploadIndex].completeStatus = true;
            this.finalImgFiles[this.uploadIndex].fileId = item.body.data[0]?.imageId;
            this.finalImgFiles[this.uploadIndex].isFileUploadCompleted = true;
          }
          if (reloadList) {
            reloadList('upload');
          }
          this.uploadIndex += 1;
          this.uploadImage(reloadList);
        }
      }, (err: any) => {
        this.finalImgFiles[this.uploadIndex].progressValue = 0;
        this.finalImgFiles[this.uploadIndex].completeStatus = false;
        this.finalImgFiles[this.uploadIndex].slideId = '';
        this.finalImgFiles[this.uploadIndex].isFileUploadCompleted = false;
        this.uploadIndex += 1;
        this.uploadImage(reloadList);
      })
    }
  }

  login(userCredentials: any) {
    return this.http.post(`${this.appConfig.apiUrl}user/login`, userCredentials);
  }

  changeRoleId(role: any) {
    this.ProjectLSData["userActiveRole"] = role;
    this.setProjectLSData();
    this.currentRoleSource.next(role);
  }

  createUser(newUserData: any) {
    return this.http.post(`${this.appConfig.apiUrl}user/create-user`, newUserData);
  }

  getRolePlaces(role: any) {
    return this.http.get(`${this.appConfig.apiUrl}user/roles/${role}/places`);
  }

  showToastMsg(message: any, type: any = 'success', duration = 3000) {
    let mainClass = 'successToast';
    if (type == 'error') {
      mainClass = 'errorToast';
    } else if (type == 'warn') {
      mainClass = 'warningToast';
    } else if (type == 'info') {
      mainClass = 'infoToast';
    }
    this.snackBar.openFromComponent(CustomSnackbarComponent, {
      data: {
        message: message,
        type: type,
        mainClass: mainClass
      },
      duration: duration,
      verticalPosition: 'top',
      horizontalPosition: 'center',
      panelClass: 'customToast'
    });
  }

  sendMail(emailId: any) {
    // http://{{Host}}:9070/qc/user/forgetUserPassword?email=onkar.yogi@airamatrix.com  
    return this.http.get(`${this.appConfig.apiUrl}user/forgetUserPassword?email=${emailId}`);
  }

  verifyCode(requestBody: any) {
    // http://{{Host}}:9070/qc/user/verifyCode
    return this.http.post(`${this.appConfig.apiUrl}user/verifyCode`, requestBody);
  }

  resetPassword(requestBody: any) {
    // http://{{Host}}:9070/qc/user/resetPassword
    return this.http.post(`${this.appConfig.apiUrl}user/resetPassword`, requestBody);
  }

  downloadReport(requestBody: any, download: boolean) {
    if (download) {
      return this.http.post(`${this.appConfig.apiUrl}slide/scanner/artifactResultReportSync`, requestBody);
    } else {
      return this.http.post(`${this.appConfig.apiUrl}slide/scanner/artifactResultReport`, requestBody, { responseType: 'blob', reportProgress: true, observe: 'events' });
    }
  }
  downloadReportFromActiveTab(requestBody: any) {

    return this.http.post(`${this.appConfig.apiUrl}slide/scanner/downloadImageReport`, requestBody, { responseType: 'blob', reportProgress: true, observe: 'events' });

  }

  loadReports(requestBody: any) {
    const additionalParams = {
      dateTimeFormat: this.appConfig.dateTimeFormat,
      zoneId: this.browserTimeZone
    }
    return this.http.post(`${this.appConfig.apiUrl}slide/downloadReport`, { ...requestBody, ...additionalParams });
  }

  removeReport(reportId: string) {
    let params = new HttpParams().set('requestedId', reportId);
    return this.http.delete(`${this.appConfig.apiUrl}slide/downloadReport`, { params });
  }

  getReportDetail(reportId: string) {
    let params = new HttpParams().set('requestedId', reportId);
    return this.http.get(`${this.appConfig.apiUrl}slide/downloadReport/download`, { params, responseType: 'blob', reportProgress: true, observe: 'events' });
  }
  cancelReport(reportId: string) {
    let params = new HttpParams().set('requestId', reportId);
    return this.http.get(`${this.appConfig.apiUrl}slide/scanner/cancelArtifactReportGen`, { params });
  }

  downloadResultJSON(requestBody: any) {
    return this.http.post(`${this.appConfig.apiUrl}slide/qc-annotation/copy`, requestBody, { responseType: 'blob', reportProgress: true, observe: 'events' });
  }

  changePassword(requestBody: any) {
    return this.http.post(`${this.appConfig.apiUrl}user/changePassword`, requestBody);
  }

  setProjectLSData() {
    localStorage[this.projectName] = JSON.stringify(this.ProjectLSData);
  }

  getUserRoleLandingPage(indvRole: any) {
    let landingPage: string;
    let userActivePlace: string;
    switch (indvRole) {
      case "SUPER_ADMIN":
        landingPage = "configure";
        userActivePlace = "configure";
        break;
      case "APPLICATION_ADMIN":
        landingPage = "dashboard";
        userActivePlace = "dashboard";
        break;
      default:
        landingPage = "gallery/allimages";
        userActivePlace = "gallery";
    }
    return { landingPage: landingPage, userActivePlace: userActivePlace };
  }

  getJobList(paramOne?: any, paramTwo?: any) {
    if (paramOne && paramTwo) {
      return this.http.get(`${this.appConfig.apiUrl}slide/jobStatus?scannerId=${paramOne}&status=${paramTwo}`);
    } else if (paramOne) {
      return this.http.get(`${this.appConfig.apiUrl}slide/jobStatus?scannerId=${paramOne}`);
    } else if (paramTwo) {
      return this.http.get(`${this.appConfig.apiUrl}slide/jobStatus?status=${paramTwo}`);
    } else {
      return this.http.get(`${this.appConfig.apiUrl}slide/jobStatus`);
    }
  }

  getJobsList(queryData: any) {
    return this.http.post(`${this.appConfig.apiUrl}slide/jobStatus`, queryData);
  }

  editBatchName(newBatchName: any, batchId: any) {
    let queryData = { name: newBatchName };
    return this.http.put(`${this.appConfig.apiUrl}slide/jobStatus/${batchId}`, queryData);
  }

  getbatchDetail(id: any) {
    return this.http.get(`${this.appConfig.apiUrl}slide/jobStatus/batch/${id}/jobs`);
  }

  getJobDetail(id: any) {
    return this.http.get(`${this.appConfig.apiUrl}slide/jobStatus/job/${id}/detail`);
  }
  // getfilteredJobList(scannerId:any, status:any){
  //   return this.http.get(`${this.appConfig.apiUrl}slide/jobStatus/scannerId=${scannerId}&&stutus=$status=${status}`);
  // }

  downloadJobDetail(jobId: any, fileType: any) {
    let queryData = {
      fileType: fileType,
      dateTimeFormat: this.appConfig.dateTimeFormat,
      zoneId: this.browserTimeZone
    }
    return this.http.post(`${this.appConfig.apiUrl}slide/jobStatus/job/${jobId}/detail/download`, queryData, { responseType: 'blob', reportProgress: true, observe: 'events' });
  }

  downloadBatchFile(batchId: any, type: any) {
    let queryData = {
      fileType: type,
      dateTimeFormat: this.appConfig.dateTimeFormat,
      zoneId: this.browserTimeZone
    }
    return this.http.post(`${this.appConfig.apiUrl}slide/jobStatus/${batchId}/download`, queryData, { responseType: 'blob', reportProgress: true, observe: 'events' });
  }

  checkBrowserZoom() {
    let zoom = Math.ceil(((window.outerWidth - 10) / window.innerWidth) * 100);
    let isBrowserZoomed: boolean = false;
    if ((zoom < 95) || (zoom > 105)) {
      isBrowserZoomed = true;
    }
    return isBrowserZoomed;
  }

  vwTOpx(value: any) {
    value = value.replace('vw', '');
    const w = window,
      d = document,
      e = d.documentElement,
      g = d.getElementsByTagName('body')[0],
      x = w.innerWidth || e.clientWidth || g.clientWidth;

    const result = (x * value) / 100;
    return result;
  }

  getArtifactReportData(reportId: any) {
    return this.http.post(`${this.appConfig.apiUrl}slide/scanner/viewArtifactResultReport`, [reportId]);
  }

  sendDashboardSaveEvent(val: boolean) {
    this.showDashboardSave.next(val);
  }

  reloadFailedLazyLoadImg(event: StateChange, slideData: any, refEl: any, pathVar: any, isForPreview: boolean = false) {
    if (event.reason == 'loading-succeeded') {
      if (this.appConfig.macroImgInfo.dimensionCheck && (pathVar == "macroPath") && (refEl.naturalWidth > refEl.naturalHeight) && !isForPreview && ((!slideData.macroRotation) || (slideData.macroRotation == 0))) {
        slideData.macroRotation += 90;
        refEl.style.transform = "rotate(" + slideData.macroRotation + "deg)";
        refEl.classList.add("rotatedImg");
        refEl?.parentNode?.parentNode?.getElementsByClassName("labelImg")?.[0]?.classList.add("rotatedImg");
        refEl?.parentNode?.parentNode?.getElementsByClassName("qrImagTag")?.[0]?.classList.add("rotatedImg");
      }
      refEl.classList.remove("ng-failed-lazyloaded");
    } else if (event.reason == 'loading-failed') {
      if (this.appConfig.failedLazyloadInfo) {
        if (slideData.retryAttempted < this.appConfig.failedLazyloadInfo.maxRetryAttempt) {
          setTimeout(() => {
            var timestamp = new Date().getTime();
            let imgPathVar = slideData[pathVar].split('?')[0];
            slideData[pathVar] = imgPathVar + '?t=' + timestamp;
            slideData.retryAttempted++;
          }, this.appConfig.failedLazyloadInfo.retryDelay);
        }
      }
    }
  }

  reloadWindow() {
    this.showRefreshPanel = false;
    this.ProjectLSData.appVersion = this.updatedAppVersion;
    this.setProjectLSData();
    window.location.reload();
  }

  checkIfImageExists(url: string, callback: any) {
    const img = new Image();
    img.src = url;

    if (img.complete) {
      callback(true);
    } else {
      img.onload = () => {
        callback(true);
      };

      img.onerror = () => {
        callback(false);
      };
    }
  };

  isLicenseFeatureEnabled() {
    return !!this.appConfig?.licenseConfig?.enabled;
  }

  startLicenseStatusPolling() {
    if (!this.isLicenseFeatureEnabled()) {
      this.stopLicenseStatusPolling();
      this.setCurrentLicenseStatus(null);
      return;
    }

    const pollingInterval = this.appConfig?.licenseConfig?.pollingInterval || 15000;
    const maxPollingAttempts = Number(this.appConfig?.licenseConfig?.maxPollingAttempts);
    this.stopLicenseStatusPolling();

    let pollingTimer$ = timer(0, pollingInterval);

    if (Number.isFinite(maxPollingAttempts) && maxPollingAttempts > 0) {
      pollingTimer$ = pollingTimer$.pipe(take(maxPollingAttempts));
    }

    this.licensePollingSubscription = pollingTimer$.pipe(
      switchMap(() => this.getLicenseStatus().pipe(
        catchError(() => of(this.ProjectLSData?.currentLicenseStatus || null))
      ))
    ).subscribe((status: any) => {
      this.setCurrentLicenseStatus(status);
    });
  }

  stopLicenseStatusPolling() {
    this.licensePollingSubscription?.unsubscribe();
    this.licensePollingSubscription = undefined;
  }

  getLicenseStatus(): Observable<any> {
    const licenseBaseUrl = this.appConfig?.licenseConfig?.licenseBaseUrl;

    if (!this.isLicenseFeatureEnabled() || !licenseBaseUrl) {
      return of(null);
    }

    return this.http.get<any>(`${licenseBaseUrl}status`);
  }

  getLicenseDueInfo(licenseInfo: any) {
    const licenseStatus = String(licenseInfo?.status || '').toUpperCase();

    if (licenseStatus === 'EXPIRED') {
      return { dueDays: 0, severityClass: 'license-critical', isExpired: true, shouldShow: true };
    }

    if (!licenseInfo?.validFrom || !licenseInfo?.validTo) {
      return { dueDays: 0, severityClass: '', isExpired: false, shouldShow: false };
    }

    const validFromTime = new Date(licenseInfo.validFrom).getTime();
    const validToTime = new Date(licenseInfo.validTo).getTime();
    const dueTime = validToTime - validFromTime;
    const dueDays = Math.max(0, Math.ceil(dueTime / (1000 * 60 * 60 * 24)));

    if (dueTime < 0 || dueDays <= 5) {
      return { dueDays, severityClass: 'license-critical', isExpired: dueTime < 0, shouldShow: true };
    }

    if (dueDays <= 30) {
      return { dueDays, severityClass: 'license-warning', isExpired: false, shouldShow: true };
    }

    return { dueDays, severityClass: '', isExpired: false, shouldShow: false };
  }
  
  uploadLicenseFile(file: File): Observable<string> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post(`${this.appConfig?.licenseConfig?.licenseBaseUrl}upload`, formData, {    responseType: 'text'   });
  }
}




